import React from 'react';
import { useAuth } from '../../context/AuthContext';
import { useUndo } from '../../context/UndoContext';
import Wordmark from './Wordmark';
import type { UserRole } from '../../types/index.js';

const API_BASE = import.meta.env.VITE_API_URL || '/_api';

const ROLE_LABELS: Record<UserRole, string> = {
  system_admin: 'System Admin',
  city_admin:   'Stadt-Admin',
  member:       'Mitglied',
  read_only:    'Lesezugriff',
};

const ROLE_COLORS: Record<UserRole, string> = {
  system_admin: 'bg-purple-100 text-purple-700',
  city_admin:   'bg-blue-100 text-blue-700',
  member:       'bg-gray-100 text-gray-700',
  read_only:    'bg-amber-100 text-amber-700',
};

const AppHeader: React.FC = () => {
  const { user } = useAuth();
  const { canUndo, stack, undoLast } = useUndo();
  if (!user) return null;

  const undoTitle = canUndo
    ? `Rückgängig: ${stack[0].label} (Strg+Z)`
    : 'Keine Aktion zum Rückgängig machen';

  // Backend returns logo_url as a server-relative path; the FastAPI server
  // is on a different origin than Vite in dev, so we prefix the API base.
  const logoSrc = user.city?.logo_url
    ? `${API_BASE}${user.city.logo_url}`
    : null;

  return (
    <header className="w-full h-[70px] bg-white border-b border-gray-200 grid grid-cols-2 sm:grid-cols-3 items-center px-4 sm:px-5 shrink-0">
      <div className="hidden sm:flex items-center gap-3 min-w-0">
        {logoSrc ? (
          <img
            src={logoSrc}
            alt={user.city?.name ?? 'Stadtlogo'}
            className="h-[50px] max-w-[200px] object-contain"
          />
        ) : user.city ? (
          <p className="text-sm text-gray-500 truncate">{user.city.name}</p>
        ) : user.role === 'system_admin' ? (
          <p className="text-sm text-gray-500 italic">System-Verwaltung</p>
        ) : null}
      </div>

      <div className="flex justify-start sm:justify-center">
        <Wordmark className="text-2xl" />
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          type="button"
          onClick={() => { if (canUndo) undoLast(); }}
          disabled={!canUndo}
          aria-label={undoTitle}
          title={undoTitle}
          className={`inline-flex items-center justify-center w-9 h-9 rounded-md text-lg ${
            canUndo
              ? 'text-slate-700 hover:bg-slate-100 hover:text-slate-900'
              : 'text-slate-300 cursor-not-allowed'
          }`}
        >
          <span aria-hidden>↶</span>
        </button>
        <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_COLORS[user.role]}`}>
          {ROLE_LABELS[user.role]}
        </span>
      </div>
    </header>
  );
};

export default AppHeader;
